'use client'
import { motion } from 'motion/react'
import React from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, FreeMode } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import { Media } from '../../components/Media'
import CardWithOverlay from '@/components/CardWithOverlay'
import { Lektionen } from '@/payload-types'
import RichText from '../RichText'
import Disclaimer from '../Disclaimer/Component'
import { formatSlug } from '@/hooks/formatSlug'

interface AcademyCardsBlockProps {
  h2Title: string
  cards?: (string | Lektionen)[] | undefined
  slug: string
  id?: string
  infoText?: any
  disclaimerText?: string
  blockName?: string
  blockType: 'academyCards'
}

type Props = AcademyCardsBlockProps & {
  className?: string
  disableInnerContainer?: boolean
}

export const CollectionImageLink: React.FC<Props> = (props) => {
  const { h2Title, cards, slug, infoText, disclaimerText } = props

  // Determine if we should show slider (mobile or >4 entries)
  const shouldShowSlider = cards && cards.length > 4

  return (
    <div className="relative">
      <section className="container relative mx-auto flex w-full flex-col gap-8 px-4 pb-0">
        <h2 className="mb-2">{h2Title}</h2>
        {disclaimerText && <Disclaimer text={disclaimerText} />}
        {infoText &&
          infoText.root &&
          infoText.root.children &&
          infoText.root.children.length > 0 &&
          infoText.root.children.some(
            (child: any) =>
              child.type === 'paragraph' &&
              child.children &&
              child.children.length > 0 &&
              child.children.some((textChild: any) => textChild.text && textChild.text.trim()),
          ) && (
            <div className="">
              <RichText data={infoText} enableGutter={false} enableProse={true} />
            </div>
          )}
        {cards && cards.length && typeof cards === 'object' && cards.length > 0 && (
          <div className="relative mb-2">
            {/* Desktop/Tablet: Grid or Slider */}
            <div className="hidden md:block">
              {shouldShowSlider ? (
                /* Swiper Slider */
                <div className="relative">
                  <Swiper
                    modules={[Navigation]}
                    slidesPerView={4}
                    spaceBetween={32} // 32px = gap-8
                    slidesPerGroup={1}
                    navigation={{
                      prevEl: '.custom-prev',
                      nextEl: '.custom-next',
                    }}
                    breakpoints={{
                      768: {
                        slidesPerView: 4,
                        spaceBetween: 32,
                      },
                    }}
                    className="academy-cards-swiper"
                  >
                    {cards.map((card, i) => {
                      if (typeof card === 'string') return null
                      return (
                        <SwiperSlide key={i}>
                          <CardWithOverlay
                            card={{
                              image: card.thumbnail,
                              title: card.headline,
                              link: {
                                slug:
                                  slug +
                                  '/' +
                                  card.slug +
                                  '/' +
                                  (card.academyLections?.[0]?.navText
                                    ? formatSlug(card.academyLections?.[0]?.navText) || ''
                                    : ''),
                              },
                            }}
                            i={i}
                          />
                        </SwiperSlide>
                      )
                    })}
                  </Swiper>

                  {/* Custom Navigation Buttons */}
                  <div className="absolute -bottom-12 right-0 z-30 flex space-x-1">
                    <button
                      className="custom-prev flex h-8 w-8 items-center justify-center bg-gray-800 text-accent transition-all duration-200 hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-gray-500"
                      aria-label="Previous slide"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <button
                      className="custom-next flex h-8 w-8 items-center justify-center bg-gray-800 text-accent transition-all duration-200 hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-gray-500"
                      aria-label="Next slide"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ) : (
                /* Static Grid Mode */
                <div className="grid grid-cols-4 gap-8">
                  {cards.map((card, i) => {
                    if (typeof card === 'string') return null
                    return (
                      <CardWithOverlay
                        key={i}
                        card={{
                          image: card.thumbnail,
                          title: card.headline,
                          link: {
                            slug:
                              slug +
                              '/' +
                              card.slug +
                              '/' +
                              (card.academyLections?.[0]?.navText
                                ? formatSlug(card.academyLections?.[0]?.navText) || ''
                                : ''),
                          },
                        }}
                        i={i}
                      />
                    )
                  })}
                </div>
              )}
            </div>

            {/* Mobile: Swiper with multiple slides and free scroll */}
            <div className="md:hidden">
              <Swiper
                modules={[Navigation, FreeMode]}
                slidesPerView="auto"
                spaceBetween={16}
                freeMode={true}
                grabCursor={true}
                navigation={{
                  prevEl: '.custom-prev-mobile',
                  nextEl: '.custom-next-mobile',
                }}
                className="academy-cards-swiper-mobile"
              >
                {cards.map((card, i) => {
                  if (typeof card === 'string') return null
                  return (
                    <SwiperSlide key={i} className="!w-[280px]">
                      <div className="h-[60vh] w-[280px]">
                        <CardWithOverlay
                          card={{
                            image: card.thumbnail,
                            title: card.headline,
                            link: {
                              slug:
                                slug +
                                '/' +
                                card.slug +
                                '/' +
                                (card.academyLections?.[0]?.navText
                                  ? formatSlug(card.academyLections?.[0]?.navText) || ''
                                  : ''),
                            },
                          }}
                          i={i}
                        />
                      </div>
                    </SwiperSlide>
                  )
                })}
              </Swiper>

              {/* Mobile Navigation */}
              <div className="mt-4 flex justify-between space-x-2 md:justify-center">
                <button className="custom-prev-mobile flex h-8 w-8 items-center justify-center bg-gray-800 text-accent">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button className="custom-next-mobile flex h-8 w-8 items-center justify-center bg-gray-800 text-accent">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
